import { useMemo, useState } from 'react';
import { useAppContext } from '@/context';
import { loadAddressByZipCodeService } from '@/services';
import { useSetFormValue } from '@/utils';
import { FormAddress, FormDeepKeys } from '@/types';
import { useErrorHandler, useToast } from '@/hooks';
import { Props, useZipCodeProps } from './types';

const getEmptyAddress = (): Partial<FormAddress> => ({
  location: undefined,
  complement: '',
  neighborhood: '',
  street: '',
  number: '',
});

export const useSingleZipcode = ({ form, setFormValue, errorHandler }: Props) => {
  const [loadingZipcode, setLoadingZipcode] = useState(false);

  const onUpdateZipcode = (zipcode: string) => {
    setFormValue('trucker.address.zipCode', zipcode);
    if (zipcode.length < 8 || loadingZipcode) return;

    setLoadingZipcode(true);

    loadAddressByZipCodeService(zipcode)
      .then((data) => {
        const address: FormAddress = { ...form.trucker.address, ...data };
        setFormValue('trucker.address', address);
      })
      .catch((error) => {
        const callback = () => {
          const data = getEmptyAddress();
          setFormValue('trucker.address', { ...form.trucker.address, ...data });
        };
        errorHandler(error, callback);
      })
      .finally(() => setLoadingZipcode(false));
  };

  const isLoadingZipcode = () => loadingZipcode;

  return { onUpdateZipcode, isLoadingZipcode };
};

export const useMultipleZipcode = ({ form, setFormValue, errorHandler }: Props) => {
  const [loadingZipCode, setLoadingZipCode] = useState<boolean[]>([]);

  const isLoadingZipcode = (position: number): boolean => loadingZipCode[position] ?? false;

  const onUpdateZipcode = (zipCode: string, position: number) => {
    const addressPath: FormDeepKeys = `truck.compositions.${position}.owner.address`;
    setFormValue(`${addressPath}.zipCode`, zipCode);
    if (zipCode.length < 8 || isLoadingZipcode(position)) return;

    setLoadingZipCode(() => Array.from(Array(form.truck.compositions.length), (_, index) => index === position));

    const currentAddress = { ...form.truck.compositions[position].owner.address, zipCode };

    loadAddressByZipCodeService(zipCode)
      .then((data) => {
        const address: FormAddress = { ...currentAddress, ...data };
        setFormValue(addressPath, address);
      })
      .catch((error: Error) => {
        const callback = () => {
          const data = getEmptyAddress();
          setFormValue(addressPath, { ...currentAddress, ...data });
        };
        errorHandler(error, callback);
      })
      .finally(() => setLoadingZipCode((old) => old.map((item, index) => (index === position ? false : item))));
  };

  return { onUpdateZipcode, isLoadingZipcode };
};

export const useZipcode = ({
  type,
}: useZipCodeProps): ReturnType<typeof useSingleZipcode | typeof useMultipleZipcode> => {
  const hooks = { single: useSingleZipcode, multiple: useMultipleZipcode };
  const { dispatch, state } = useAppContext();
  const setFormValue = useSetFormValue(dispatch);
  const form = useMemo(() => state.risk.data, [state.risk.data]);
  const toast = useToast();

  const defaultErrorHandler = useErrorHandler((error) => {
    toast.add({ type: 'error', title: error.message });
  });

  const errorHandler = (error: Error, cb: () => void) => {
    defaultErrorHandler(error);
    cb();
  };

  return hooks[type]({ form, setFormValue, errorHandler });
};
